// Simulation core: slow wander (5-7 px/s, retarget every few seconds)
// with social bias + bed-seeking; distance-only encounters; energy model
// (social drains, solitude +1/10s, beds 4x); cooldowns. Pure + tested.
import { TUNING } from '../constants.js';
import { breakSteer, mingleSteer, steerToward } from './mingle.js';
import type { Bed, Direction, RuntimeNpc, SleepState } from '../types.js';

export interface SimWorld {
  size: number; // square room px (legacy default when width/height absent)
  now: number; // sim ms clock (pausable — single clock for movement + bubbles)
  width?: number;
  height?: number;
  mingle?: boolean;
  onBreak?: boolean;
  breakRolls?: { pause: number; x: number; y: number };
  beds?: Bed[]; // optional so unit tests can opt out of furniture
  // Social counsel: who should self pursue? Returns the chosen other (or null
  // to fall back to nearest). Provided by the UI layer, which owns
  // relationships + chat recency; pure given those inputs.
  counsel?: (selfId: string, others: RuntimeNpc[]) => RuntimeNpc | null;
}

// A bed is available when nobody is crashed in it.
export function bedTaken(bed: Bed, others: RuntimeNpc[]): boolean {
  return others.some((o) => o.restingBed === bed.id);
}

// Bed under a point (for grab-dragging). Radius covers the 44x32 frame.
export function findBedAt(beds: Bed[], x: number, y: number, radius = 26): Bed | null {
  let best: Bed | null = null;
  let bestD = radius;
  for (const b of beds) {
    const d = Math.hypot(b.x - x, b.y - y);
    if (d <= bestD) {
      bestD = d;
      best = b;
    }
  }
  return best;
}
// Nearest free bed within maxDist, if any. Shared by steering (where to
// walk) and pairing (bed-seekers are do-not-disturb) so the two agree.
export function nearestFreeBed(
  self: RuntimeNpc,
  others: RuntimeNpc[],
  beds: Bed[] | undefined,
  maxDist: number,
): Bed | null {
  let bed: Bed | null = null;
  let best: number = maxDist;
  for (const b of beds ?? []) {
    if (bedTaken(b, others)) continue;
    const d = Math.hypot(b.x - self.x, b.y - self.y);
    if (d < best) {
      best = d;
      bed = b;
    }
  }
  return bed;
}

// Sleep depth from continuous bed time. Not in bed (or just settling) reads
// as awake — the bio labels settling separately via restingBed.
export function sleepDepth(now: number, r: { restingBed: string | null; bedSince: number }): SleepState {
  if (!r.restingBed) return 'awake';
  const s = (now - r.bedSince) / 1000;
  if (s >= TUNING.bedDeepAfterS) return 'deep';
  if (s >= TUNING.bedNapAfterS) return 'napping';
  return 'awake';
}

const rand = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);

export function pickSpeed(): number {
  return rand(TUNING.minSpeedPxS, TUNING.maxSpeedPxS);
}

export function pickRetargetDelay(now: number): number {
  const [lo, hi] = TUNING.retargetEveryS;
  return now + rand(lo, hi) * 1000;
}

// Choose a wander vector: random direction + bias. Tired NPCs (energy ≤ 3)
// gently seek a free nearby bed first; otherwise bias toward/away the
// nearest other NPC based on energy (high seeks, low avoids) and cooldowns.
// Returns velocity components (px/s).
export function pickWanderVector(
  self: RuntimeNpc,
  others: RuntimeNpc[],
  energy: number,
  world?: SimWorld,
): { vx: number; vy: number } {
  const ang = rand(0, Math.PI * 2);
  let dx = Math.cos(ang);
  let dy = Math.sin(ang);

  // NOTE: retargetAt doubles as the clock here — this fn only runs at
  // retarget instants, when stepNpc has just stamped it with world.now.
  const now = self.retargetAt; // retarget timestamp used as clock proxy
  const onCooldown = now < self.cooldownUntil || now < self.avoidUntil;

  // Radial push-talk overrides everything: beeline for the chosen NPC.
  if (self.pushTargetId && now < self.pushUntil) {
    const target = others.find((o) => o.npcId === self.pushTargetId);
    if (target) {
      const d = Math.hypot(target.x - self.x, target.y - self.y);
      if (d > 1) {
        const w = TUNING.pushSteerWeight;
        dx = dx * (1 - w) + ((target.x - self.x) / d) * w;
        dy = dy * (1 - w) + ((target.y - self.y) / d) * w;
        const len = Math.hypot(dx, dy) || 1;
        dx /= len;
        dy /= len;
        const sp = self.speedPxS || pickSpeed();
        return { vx: dx * sp, vy: dy * sp };
      }
    }
  }

  // Tired NPCs gently seek a free nearby bed before any social steering.
  if (energy <= TUNING.avoidThreshold && world?.beds?.length) {
    const bed = nearestFreeBed(self, others, world.beds, TUNING.bedSeekRadiusPx);
    if (bed) {
      const bedDist = Math.hypot(bed.x - self.x, bed.y - self.y);
      if (bedDist > 1) {
        const w = 0.5; // gentle: beds beckon, they don't compel
        dx = dx * (1 - w) + ((bed.x - self.x) / bedDist) * w;
        dy = dy * (1 - w) + ((bed.y - self.y) / bedDist) * w;
        const len = Math.hypot(dx, dy) || 1;
        dx /= len;
        dy /= len;
        const sp = self.speedPxS || pickSpeed();
        return { vx: dx * sp, vy: dy * sp };
      }
    }
  }
  // Social candidates: free others (resting NPCs stay visible beacons).
  const free: RuntimeNpc[] = [];
  for (const o of others) {
    if (o.npcId === self.npcId || o.busyWith) continue;
    free.push(o);
  }
  let nearest: RuntimeNpc | null = null;
  let best = Infinity;
  for (const o of free) {
    const d = Math.hypot(o.x - self.x, o.y - self.y);
    if (d < best) {
      best = d;
      nearest = o;
    }
  }
  // Counsel (urge-to-chat) overrides nearest when seeking: pursue the person
  // you most want to talk to, not just the closest body.
  let target = nearest;
  let targetDist = best;
  if (target && energy >= TUNING.seekThreshold && !onCooldown && world?.counsel) {
    const pick = world.counsel(self.npcId, free);
    if (pick) {
      target = pick;
      targetDist = Math.hypot(pick.x - self.x, pick.y - self.y);
    }
  }
  if (target && targetDist > 1) {
    const toward = energy >= TUNING.seekThreshold && !onCooldown;
    const away = energy <= TUNING.avoidThreshold || onCooldown;
    if (toward || away) {
      // Magnitude scales with extremeness: mid-energy drift is near-random,
      // extreme energy steers hard (up to socialBiasWeight at 1 or 10).
      const extreme = Math.min(1, Math.abs(energy - 5.5) / 4.5);
      const w = Math.min(0.9, TUNING.socialBiasWeight * (0.5 + extreme));
      const s = away ? -1 : 1;
      dx = dx * (1 - w) + s * ((target.x - self.x) / targetDist) * w;
      dy = dy * (1 - w) + s * ((target.y - self.y) / targetDist) * w;
      const len = Math.hypot(dx, dy) || 1;
      dx /= len;
      dy /= len;
    }
  }
  // Gentle wall-avoid: favour open floor at every energy, so wanderers don't
  // hug walls and pin into corners. Skipped without a world (unit tests).
  if (world) {
    const roomW = world.width ?? world.size;
    const roomH = world.height ?? world.size;
    const m = TUNING.wallAvoidMarginPx;
    const wght = TUNING.wallAvoidWeight;
    let px = 0;
    let py = 0;
    if (self.x < m) px += (1 - self.x / m) * wght;
    if (roomW - self.x < m) px -= (1 - (roomW - self.x) / m) * wght;
    if (self.y < m) py += (1 - self.y / m) * wght;
    if (roomH - self.y < m) py -= (1 - (roomH - self.y) / m) * wght;
    if (px !== 0 || py !== 0) {
      dx += px;
      dy += py;
      const len = Math.hypot(dx, dy) || 1;
      dx /= len;
      dy /= len;
    }
  }
  const sp = self.speedPxS || pickSpeed();
  return { vx: dx * sp, vy: dy * sp };
}

// Walkable floor for the sprite anchor: keep arms, head, and feet inside the
// interior (inside the wall band drawn from each outer edge).
export function floorAnchorBounds(
  sizeOrWidth: number,
  height?: number,
): { minX: number; maxX: number; minY: number; maxY: number } {
  const w = TUNING.wallThicknessPx;
  const roomW = sizeOrWidth;
  const roomH = height ?? sizeOrWidth;
  let minX = w + 12;
  let maxX = roomW - w - 12;
  let minY = w + 25;
  let maxY = roomH - w - 20;
  const cx = roomW / 2;
  const cy = roomH / 2;
  if (minX > maxX) minX = maxX = cx;
  if (minY > maxY) minY = maxY = cy;
  return { minX, maxX, minY, maxY };
}

export function stepNpc(
  npc: RuntimeNpc,
  others: RuntimeNpc[],
  energy: number,
  world: SimWorld,
  dtS: number,
): RuntimeNpc {
  const n = { ...npc };
  if (n.busyWith) return n; // talking NPCs stand still
  if (n.restingBed) return { ...n, moving: false }; // crashed: wake handled in tickWorld
  const roomW = world.width ?? world.size;
  const roomH = world.height ?? world.size;
  const bounds = floorAnchorBounds(roomW, world.height !== undefined ? roomH : undefined);
  const { minX, maxX, minY, maxY } = bounds;
  if (world.now >= n.retargetAt) {
    if (world.mingle && world.onBreak) {
      const pushTarget = n.pushTargetId && world.now < n.pushUntil
        ? others.find((o) => o.npcId === n.pushTargetId)
        : undefined;
      const mv = pushTarget
        ? steerToward(n, pushTarget, 400)
        : breakSteer(
          n,
          bounds,
          world.breakRolls?.pause ?? Math.random(),
          world.breakRolls?.x ?? Math.random(),
          world.breakRolls?.y ?? Math.random(),
        );
      n.vx = mv.vx;
      n.vy = mv.vy;
      n.speedPxS = Math.hypot(mv.vx, mv.vy);
      n.retargetAt = world.now + mv.retargetMs;
    } else if (world.mingle) {
      const free = others.filter((o) => o.npcId !== n.npcId && !o.busyWith);
      let nearest: RuntimeNpc | null = null;
      let best = Infinity;
      for (const o of free) {
        const d = Math.hypot(o.x - n.x, o.y - n.y);
        if (d < best) {
          best = d;
          nearest = o;
        }
      }
      const mv = nearest && best > TUNING.encounterDistPx
        ? steerToward(n, nearest, 400)
        : mingleSteer(n, others, Math.random(), Math.floor(Math.random() * 1000), TUNING.encounterDistPx);
      n.vx = mv.vx;
      n.vy = mv.vy;
      n.speedPxS = Math.hypot(mv.vx, mv.vy);
      n.retargetAt = world.now + mv.retargetMs;
    } else {
      const v = pickWanderVector({ ...n, retargetAt: world.now }, others, energy, world);
      n.vx = v.vx;
      n.vy = v.vy;
      n.speedPxS = Math.hypot(v.vx, v.vy);
      n.retargetAt = pickRetargetDelay(world.now);
    }
  }
  const px = n.x + n.vx * dtS;
  const py = n.y + n.vy * dtS;
  const cx = Math.min(maxX, Math.max(minX, px));
  const cy = Math.min(maxY, Math.max(minY, py));
  // Wall-slide: a clamped axis loses its into-wall component so NPCs slide
  // along walls instead of pinning into corners looking frozen. Cornered
  // NPCs hold one frame and re-pick within ~400ms to escape.
  const hitX = cx !== px;
  const hitY = cy !== py;
  if (hitX && hitY) {
    n.retargetAt = Math.min(n.retargetAt, world.now + 400);
  } else {
    if (hitX) n.vx = 0;
    if (hitY) n.vy = 0;
    if (hitX || hitY) n.retargetAt = Math.min(n.retargetAt, world.now + 800);
  }
  n.x = cx;
  n.y = cy;
  // Face dominant axis (Stardew 4-dir).
  n.dir = directionOf(n.vx, n.vy);
  n.moving = Math.hypot(n.vx, n.vy) > 0.01;
  return n;
}

export function directionOf(vx: number, vy: number): Direction {
  if (Math.abs(vx) >= Math.abs(vy)) return vx >= 0 ? 'right' : 'left';
  return vy >= 0 ? 'down' : 'up';
}

export function dist(a: RuntimeNpc, b: RuntimeNpc): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

// Encounter scan: return pairs (i<j) in range, both free, cooldowns expired.
// Talking NPCs never pair. Sleepers: settling + deep are undisturbed, but
// napping CAN be approached (wakes groggy). NOTE: avoidUntil (busy-third-wheel
// backoff) deliberately does NOT gate pairing — it only steers wandering.
// Push-talk ("talk ONLY to X") gates pairing per-NPC and bypasses the
// post-chat cooldown for the pushed pair, so a nudge always works.
// Bed-seekers (tired NPCs homing on a free bed) are do-not-disturb: without
// this, two homers meet at 140px encounter range long before the 26px crash
// radius and chat-loop forever instead of sleeping. Push overrides that too.
export function findEncounters(
  npcs: RuntimeNpc[],
  now: number,
  dnd: Set<string> = new Set(),
): Array<[RuntimeNpc, RuntimeNpc]> {
  const pairs: Array<[RuntimeNpc, RuntimeNpc]> = [];
  const taken = new Set<string>();
  const pushOn = (r: RuntimeNpc): boolean => !!r.pushTargetId && now < r.pushUntil;
  // Any crash is undisturbed — settling, napping, and deep alike. Only a
  // push-talk nudge wakes a sleeper (captured groggy for the LLM at gen time).
  const undisturbed = (r: RuntimeNpc): boolean => !!r.restingBed;
  for (let i = 0; i < npcs.length; i++) {
    const a = npcs[i];
    if (a.busyWith || taken.has(a.npcId)) continue;
    for (let j = i + 1; j < npcs.length; j++) {
      const b = npcs[j];
      if (b.busyWith || taken.has(b.npcId)) continue;
      // Exclusive interest: a pushed NPC only accepts its target (and vice versa).
      if (pushOn(a) && b.npcId !== a.pushTargetId) continue;
      if (pushOn(b) && a.npcId !== b.pushTargetId) continue;
      // Cooldown + undisturbed-sleep + bed-seeker vetoes — all bypassed when
      // this exact pair is actively pushed (a nudge always works, even on
      // deep sleep or mid-homer).
      const pushedPair =
        (a.pushTargetId === b.npcId && now < a.pushUntil) ||
        (b.pushTargetId === a.npcId && now < b.pushUntil);
      if (!pushedPair) {
        if (dnd.has(a.npcId) || dnd.has(b.npcId)) continue;
        if (now < a.cooldownUntil || now < b.cooldownUntil) continue;
        if (undisturbed(a) || undisturbed(b)) continue;
      }
      if (dist(a, b) <= TUNING.encounterDistPx) {
        pairs.push([a, b]);
        taken.add(a.npcId);
        taken.add(b.npcId);
        break;
      }
    }
  }
  return pairs;
}

// Clamp helper for relationship deltas.
export function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
